import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ArrowRight, 
  Search, 
  Calendar, 
  ListChecks, 
  ShoppingCart,
  ChefHat,
  Clock,
  Leaf,
  Sparkles
} from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      number: '01',
      title: 'Find Recipes',
      description: 'Browse or search meals that fit your schedule and taste.',
      icon: Search,
      color: 'bg-primary/10 text-primary'
    },
    {
      number: '02',
      title: 'Plan Your Week',
      description: 'Drag recipes onto your calendar in seconds.',
      icon: Calendar,
      color: 'bg-accent/30 text-accent-foreground'
    },
    {
      number: '03',
      title: 'Generate Grocery List',
      description: 'Automatically combine ingredients into one clean list.',
      icon: ListChecks,
      color: 'bg-secondary text-secondary-foreground'
    },
    {
      number: '04',
      title: 'Shop Instantly',
      description: 'Send your list directly to Instacart or your favorite store.',
      icon: ShoppingCart,
      color: 'bg-green-100 text-green-700'
    }
  ];

  const stats = [
    { value: '50K+', label: 'Curated Recipes', icon: ChefHat },
    { value: '15 min', label: 'Average Planning Time', icon: Clock },
    { value: 'Up to 30%', label: 'Less Food Waste', icon: Leaf }
  ];

  return (
    <div className="min-h-screen" data-testid="how-it-works-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <ChefHat className="h-8 w-8 text-primary" />
            <div className="flex flex-col">
              <span className="text-xl font-semibold font-['Playfair_Display']">Harvest & Hearth</span>
              <span className="text-[10px] text-muted-foreground hidden sm:block">
                Plan Once. Cart Done.
              </span>
            </div>
          </Link>
          <Link to="/auth">
            <Button className="rounded-full" data-testid="nav-get-started">
              Get Started Free
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold font-['Playfair_Display'] mb-6">
            From recipe to grocery cart — <span className="text-primary italic">in minutes.</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            See how Harvest & Hearth helps you plan meals, generate grocery lists, and shop faster.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <Card key={index} className="border-border/50 bg-background relative overflow-hidden group hover:shadow-lg transition-shadow">
                <CardContent className="pt-8 pb-6">
                  <span className="absolute top-4 right-4 text-6xl font-bold text-muted/20 font-['Playfair_Display']">
                    {step.number}
                  </span>
                  <div className={`h-14 w-14 rounded-2xl ${step.color} flex items-center justify-center mb-6`}>
                    <step.icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-semibold font-['Playfair_Display'] mb-3">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {step.description}
                  </p>
                </CardContent>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="h-6 w-6 text-muted-foreground/30" />
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mid CTA Section */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
            <Sparkles className="h-4 w-4" />
            No credit card required
          </div>
          <h2 className="text-3xl md:text-4xl font-semibold font-['Playfair_Display'] mb-4">
            Try it free — start planning today
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Create your account in seconds and see how easy meal planning can be.
          </p>
          <Link to="/auth">
            <Button size="lg" className="rounded-full h-14 px-10 text-lg btn-lift" data-testid="mid-cta">
              Get Started Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Stats/Proof Section */}
      <section className="py-16 bg-secondary/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="h-6 w-6 text-primary" />
                </div>
                <p className="text-4xl md:text-5xl font-bold text-primary font-['Playfair_Display'] mb-2">
                  {stat.value}
                </p>
                <p className="text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold font-['Playfair_Display'] mb-4">
            Ready to simplify your week?
          </h2>
          <p className="text-muted-foreground mb-8">
            Join thousands of home cooks saving time and eating better.
          </p>
          <Link to="/auth">
            <Button size="lg" className="rounded-full h-14 px-10 text-lg btn-lift" data-testid="final-cta">
              Start Planning Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <ChefHat className="h-6 w-6 text-primary" />
              <span className="font-semibold font-['Playfair_Display']">Harvest & Hearth</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 Harvest & Hearth. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorks;
